import React, { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Bell, Check } from "lucide-react"
import useLearnerNotifications from "../../hooks/useLearnerNotifications"
import { formatRelativeTime } from "../../utils/notificationDisplay"
import { learnerNotificationHref } from "./config/studentNavItems"
import "./NotificationBell.style.scss"

const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useLearnerNotifications()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const openNotification = (id: string, data: Parameters<typeof learnerNotificationHref>[0]) => {
    const href = learnerNotificationHref(data)
    setIsOpen(false)
    if (href) navigate(href)
    void markAsRead(id)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen])

  return (
    <div className="student-notification-bell" ref={dropdownRef}>
      <button
        type="button"
        className="student-notification-bell__trigger"
        aria-label="Notifications"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Bell className="student-notification-bell__icon" />
        {unreadCount > 0 && (
          <span className="student-notification-bell__badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="student-notification-bell__dropdown">
          <div className="student-notification-bell__header">
            <h4>Notifications</h4>
            {unreadCount > 0 && (
              <button onClick={markAllAsRead} className="student-notification-bell__mark-all">
                <Check size={14} /> Mark all read
              </button>
            )}
          </div>
          
          <div className="student-notification-bell__list">
            {notifications.length === 0 ? (
              <div className="student-notification-bell__empty">
                No new notifications
              </div>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.id}
                  className="student-notification-bell__item"
                  onClick={() => openNotification(notif.id, notif)}
                >
                  <div className="student-notification-bell__item-title">{notif.title}</div>
                  {notif.message && <div className="student-notification-bell__item-msg">{notif.message}</div>}
                  {notif.created_at && (
                    <div className="student-notification-bell__item-time">
                      {formatRelativeTime(notif.created_at) || "Just now"}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
